import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Rating,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Badge,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  ViewList as ListIcon,
  CalendarMonth as CalendarIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Schedule as TimeIcon,
  AttachMoney as MoneyIcon,
  VerifiedUser as VerifiedIcon,
  Luggage as LuggageIcon,
  ChildCare as ChildCareIcon,
  Accessible as AccessibleIcon,
  Pets as PetsIcon,
  SmokingRooms as SmokingIcon,
  Search as SearchIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { useAuth } from '../../contexts/AuthContext';
import RideDetailsModal from '../RideDetailsModal';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../services/apiService';
import MessageButton from '../Chat/MessageButton';
import TranslatingText from './TranslatingText';

interface Ride {
  id: string;
  driver: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    driverRating: number;
    profilePicture?: string;
    isLicenseVerified?: boolean;
    cancellationBadgeUntil?: string | Date;
  };
  originCity: {
    id: string;
    name: string;
    province: string;
  };
  destinationCity: {
    id: string;
    name: string;
    province: string;
  };
  originDetails?: string;
  destinationDetails?: string;
  departureDateTime: string;
  pricePerPerson: number;
  availableSeats: number;
  totalSeats?: number;
  estimatedDurationMinutes?: number;
  estimatedDuration?: number;
  vehicle?: {
    make: { id: string; name: string };
    model: { id: string; name: string };
    color: string;
    isDocsVerified?: boolean;
  };
  allowsLargeLuggage?: boolean;
  allowsPets?: boolean;
  allowsSmoking?: boolean;
  additionalNotes?: string;
  allowChat?: boolean;
}

interface RideRequest {
  id: string;
  passenger: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    passengerRating?: number;
    profilePicture?: string;
    cancellationBadgeUntil?: string | Date;
  };
  originCity: {
    id: string;
    name: string;
    province: string;
  };
  destinationCity: {
    id: string;
    name: string;
    province: string;
  };
  originDetails?: string;
  destinationDetails?: string;
  preferredDateTime: string;
  timeFlexibility: number;
  passengerCount: number;
  minBudget?: number;
  maxBudget?: number;
  needsLargeLuggage?: boolean;
  needsChildSeat?: boolean;
  needsWheelchairAccess?: boolean;
  specialRequirements?: string;
  description?: string;
  status: string;
  expiresAt: string;
  allowChat?: boolean;
  bids?: Array<{
    id: string;
    priceOffer: number;
    status: string;
    message?: string;
    driver: {
      id: string;
      firstName: string;
      lastName: string;
      preferredName?: string;
    };
  }>;
}

type SearchResult = Ride | RideRequest;

interface ResultsModalProps {
  open: boolean;
  onClose: () => void;
  results: SearchResult[];
  searchType: 'offered' | 'requested';
  originCity?: string;
  destinationCity?: string;
  error?: string;
}

const ResultsModal: React.FC<ResultsModalProps> = ({
  open,
  onClose,
  results,
  searchType,
  originCity,
  destinationCity,
  error,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedItem, setSelectedItem] = useState<SearchResult | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [priceFilter, setPriceFilter] = useState<{ min?: number; max?: number }>({});
  const [dateFilter, setDateFilter] = useState<Date | null>(null);
  const [sortBy, setSortBy] = useState<'price' | 'date'>('date');
  const { user } = useAuth();
  const isDriver = user?.role === 'DRIVER';
  const [joiningRide, setJoiningRide] = useState(false);
  const { openProfile } = useUserProfile();
  const [savedRideIds, setSavedRideIds] = useState<Set<string>>(new Set());

  // Check if saved on mount/open
  React.useEffect(() => {
    if (open && user && searchType === 'offered') {
      fetchSavedRides();
    }
  }, [open, user, searchType]);

  const fetchSavedRides = async () => {
    try {
      const response = await apiService.get('/saved-rides');
      if (response.success) {
        setSavedRideIds(new Set(response.data.map((r: any) => r.id)));
      }
    } catch (error) {
      console.error('Fetch saved rides error:', error);
    }
  };

  const handleToggleSave = async (e: React.MouseEvent, rideId: string) => {
    e.stopPropagation();
    if (!user) {
      alert(t('rideDetails.saveLoginRequiredMessage', 'Please sign in to save rides for later.'));
      return;
    }

    try {
      const response = await apiService.post(`/saved-rides/${rideId}`);
      if (response.success) {
        setSavedRideIds(prev => {
          const next = new Set(prev);
          if ((response as any).saved) next.add(rideId);
          else next.delete(rideId);
          return next;
        });
      }
    } catch (error) {
      console.error('Toggle save error:', error);
    }
  };

  const handleProfileClick = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    openProfile(userId);
  };

  // Function to handle redirect to become driver
  const handleBecomeDriverRedirect = () => {
    // Get current page URL to return to
    const returnUrl = location.pathname + location.search;
    navigate('/become-driver', {
      state: { returnUrl }
    });
  };
  // Per-request bid state for inline bidding
  const [requestBids, setRequestBids] = useState<{ [key: string]: { price: string; message: string; submitting: boolean; error: string } }>({});
  const [detailBidPrice, setDetailBidPrice] = useState('');
  const [detailBidMessage, setDetailBidMessage] = useState('');
  const [submittingDetailBid, setSubmittingDetailBid] = useState(false);
  const [detailBidError, setDetailBidError] = useState('');

  const isRideRequest = (item: SearchResult): item is RideRequest => {
    return 'passenger' in item && 'preferredDateTime' in item;
  };

  const isRide = (item: SearchResult): item is Ride => {
    return 'driver' in item && 'departureDateTime' in item;
  };

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
  };

  // Filter and sort results
  const filteredResults = useMemo(() => {
    let filtered = [...results];

    // Filter by price
    if (priceFilter.min !== undefined || priceFilter.max !== undefined) {
      filtered = filtered.filter((item) => {
        if (isRide(item)) {
          const price = item.pricePerPerson;
          if (priceFilter.min !== undefined && price < priceFilter.min) return false;
          if (priceFilter.max !== undefined && price > priceFilter.max) return false;
          return true;
        } else if (isRideRequest(item)) {
          // For ride requests, check if price is within budget range
          if (item.minBudget && priceFilter.max && item.minBudget > priceFilter.max) return false;
          if (item.maxBudget && priceFilter.min && item.maxBudget < priceFilter.min) return false;
          return true;
        }
        return true;
      });
    }

    // Filter by date
    if (dateFilter) {
      filtered = filtered.filter((item) => {
        const dateTime = isRide(item) ? item.departureDateTime : item.preferredDateTime;
        const itemDate = new Date(dateTime);
        const filterDate = new Date(dateFilter);
        return (
          itemDate.getDate() === filterDate.getDate() &&
          itemDate.getMonth() === filterDate.getMonth() &&
          itemDate.getFullYear() === filterDate.getFullYear()
        );
      });
    }

    // Sort results
    filtered.sort((a, b) => {
      if (sortBy === 'price') {
        const priceA = isRide(a) ? a.pricePerPerson : (a.minBudget || 0);
        const priceB = isRide(b) ? b.pricePerPerson : (b.minBudget || 0);
        return priceA - priceB;
      } else {
        const dateA = new Date(isRide(a) ? a.departureDateTime : a.preferredDateTime);
        const dateB = new Date(isRide(b) ? b.departureDateTime : b.preferredDateTime);
        return dateA.getTime() - dateB.getTime();
      }
    });

    return filtered;
  }, [results, priceFilter, dateFilter, sortBy]);

  // Group results by date for calendar view
  const getResultsByDate = () => {
    const resultsByDate: { [key: string]: SearchResult[] } = {};
    filteredResults.forEach((item) => {
      const dateTime = isRide(item) ? item.departureDateTime : item.preferredDateTime;
      const dateKey = new Date(dateTime).toISOString().split('T')[0];
      if (!resultsByDate[dateKey]) {
        resultsByDate[dateKey] = [];
      }
      resultsByDate[dateKey].push(item);
    });
    return resultsByDate;
  };

  const resultsByDate = getResultsByDate();

  // Get results for selected date in calendar
  const getFilteredResults = () => {
    if (viewMode === 'list' || !selectedDate) {
      return filteredResults;
    }
    const dateKey = selectedDate.toISOString().split('T')[0];
    return resultsByDate[dateKey] || [];
  };

  const displayResults = getFilteredResults();

  // Custom calendar day component
  const CustomDay = (props: any) => {
    const { day, ...other } = props;
    const dateKey = day.toISOString().split('T')[0];
    const dayResults = resultsByDate[dateKey] || [];
    const resultCount = dayResults.length;

    return (
      <Badge
        key={day.toString()}
        overlap="circular"
        badgeContent={resultCount > 0 ? resultCount : undefined}
        color="primary"
      >
        <PickersDay
          {...other}
          day={day}
          sx={{
            ...(resultCount > 0 && {
              fontWeight: 'bold',
              border: '2px solid',
              borderColor: 'primary.main',
            }),
          }}
        />
      </Badge>
    );
  };

  const renderRideCard = (ride: Ride) => {
    const { date, time } = formatDateTime(ride.departureDateTime);

    return (
      <Card
        key={ride.id}
        sx={{
          cursor: 'pointer',
          position: 'relative',
          overflow: 'visible',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '24px',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: '0 12px 40px rgba(16, 185, 129, 0.15)',
            borderColor: '#10b981',
            '& .ride-accent': {
              opacity: 1,
            }
          },
        }}
        onClick={() => {
          setSelectedItem(ride);
          setDetailDialogOpen(true);
        }}
      >
        {/* Top Accent */}
        <Box
          className="ride-accent"
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            right: 0,
            height: '6px',
            bgcolor: '#10b981',
            borderRadius: '24px 24px 0 0',
            transition: 'all 0.4s ease',
            opacity: 0.6
          }}
        />

        {/* Save Button */}
        <IconButton
          onClick={(e) => handleToggleSave(e, ride.id)}
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            zIndex: 10,
            bgcolor: 'rgba(255,255,255,0.9)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              bgcolor: 'white',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          {savedRideIds.has(ride.id) ? (
            <BookmarkIcon sx={{ color: '#10b981' }} />
          ) : (
            <BookmarkBorderIcon sx={{ color: '#6b7280' }} />
          )}
        </IconButton>

        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header with Driver & Rating */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  ride.driver.isLicenseVerified ? (
                    <VerifiedIcon sx={{ color: '#4caf50', fontSize: 16, bgcolor: 'white', borderRadius: '50%' }} />
                  ) : ride.driver.cancellationBadgeUntil && new Date(ride.driver.cancellationBadgeUntil) > new Date() ? (
                    <Typography
                      sx={{
                        width: 18,
                        height: 18,
                        bgcolor: '#ef4444',
                        color: 'white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: '900',
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      title={t('rideDetails.cancellationBadgeTooltip', 'Recent Cancellation Badge')}
                    >
                      C
                    </Typography>
                  ) : null
                }
              >
                <Avatar
                  src={ride.driver.profilePicture}
                  sx={{
                    width: 48,
                    height: 48,
                    cursor: 'pointer',
                    bgcolor: '#10b981',
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                  }}
                  onClick={(e) => handleProfileClick(e, ride.driver.id)}
                >
                  {ride.driver.firstName[0]}
                </Avatar>
              </Badge>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1a1a1a' }}>
                  {ride.driver.preferredName || ride.driver.firstName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Rating value={ride.driver.driverRating || 0} readOnly size="small" precision={0.1} sx={{ fontSize: '0.9rem' }} />
                  <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 700, color: '#6b7280' }}>
                    {ride.driver.driverRating?.toFixed(1)}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Typography variant="h5" sx={{
              fontWeight: 900,
              color: '#10b981',
              fontSize: '1.5rem',
              letterSpacing: '-0.03em'
            }}>
              ${ride.pricePerPerson}
            </Typography>
          </Box>

          {/* Route Info */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mr: 2,
                height: '40px',
                justifyContent: 'space-between'
              }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', border: '2px solid #10b981' }} />
                <Box sx={{ width: '2px', flex: 1, bgcolor: '#e5e7eb', my: 0.5 }} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a', lineHeight: 1.2, mb: 0.5 }}>
                  {ride.originCity.name}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a', lineHeight: 1.2 }}>
                  {ride.destinationCity.name}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

          {/* Date & Seats */}
          <Box sx={{ mt: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '1.1rem' }}>
                <CalendarIcon sx={{ fontSize: 16 }} />
                {date}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '1.1rem' }}>
                <TimeIcon sx={{ fontSize: 16 }} />
                {time}
              </Typography>
            </Box>

            <Chip
              label={t('searchResults.seatsLeft', { count: ride.availableSeats })}
              size="small"
              sx={{
                width: '100%',
                bgcolor: ride.availableSeats > 1 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                color: ride.availableSeats > 1 ? '#10b981' : '#ef4444',
                fontWeight: 800,
                fontSize: '0.9rem',
                border: 'none',
                height: '32px'
              }}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  const handleSubmitBidInline = async (request: RideRequest) => {
    const requestBid = requestBids[request.id] || { price: '', message: '', submitting: false, error: '' };
    const price = parseFloat(requestBid.price);

    if (isNaN(price) || price <= 0) {
      setRequestBids(prev => ({
        ...prev,
        [request.id]: { ...requestBid, error: t('searchResults.requestDetails.priceError', 'Please enter a valid price') }
      }));
      return;
    }

    if (request.minBudget && price < request.minBudget) {
      setRequestBids(prev => ({
        ...prev,
        [request.id]: { ...requestBid, error: t('searchResults.requestDetails.minPriceError', { price: request.minBudget }) }
      }));
      return;
    }

    if (request.maxBudget && price > request.maxBudget) {
      setRequestBids(prev => ({
        ...prev,
        [request.id]: { ...requestBid, error: t('searchResults.requestDetails.maxPriceError', { price: request.maxBudget }) }
      }));
      return;
    }

    setRequestBids(prev => ({
      ...prev,
      [request.id]: { ...requestBid, submitting: true, error: '' }
    }));

    try {
      const bidData = {
        requestId: request.id,
        priceOffer: price,
        message: requestBid.message.trim() || undefined
      };

      const response = await apiService.post('/bids', bidData);

      if (response.success) {
        setRequestBids(prev => ({
          ...prev,
          [request.id]: { price: '', message: '', submitting: false, error: '' }
        }));

        // Refresh the request to show the new bid
        try {
          const updatedResponse = await apiService.get(`/requests/${request.id}`);
          if (updatedResponse.success && updatedResponse.data?.request) {
            // Update the results array
            const updatedResults = results.map(item =>
              item.id === request.id ? updatedResponse.data.request : item
            );
            // Note: We can't directly update results prop, but we can trigger a refresh
            // For now, just show success - parent component should refresh
            alert(t('searchResults.requestDetails.bidSuccess', 'Bid submitted successfully!'));
          }
        } catch (refreshError) {
          console.error('Error refreshing request:', refreshError);
        }
      }
    } catch (error: any) {
      console.error('Submit bid error:', error);
      setRequestBids(prev => ({
        ...prev,
        [request.id]: {
          ...requestBid,
          submitting: false,
          error: error.response?.data?.message || t('searchResults.requestDetails.bidError', 'Failed to submit bid')
        }
      }));
    }
  };

  const renderRequestCard = (request: RideRequest) => {
    const { date, time } = formatDateTime(request.preferredDateTime);
    const hasExistingBid = isDriver && request.bids?.some(bid => bid.driver.id === user?.id);
    const requestBid = requestBids[request.id] || { price: '', message: '', submitting: false, error: '' };
    const showBidForm = isDriver && !hasExistingBid && request.status === 'OPEN';

    return (
      <Card
        key={request.id}
        sx={{
          position: 'relative',
          overflow: 'visible',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '24px',
          border: '2px solid',
          borderColor: 'rgba(99, 102, 241, 0.1)',
          boxShadow: '0 4px 20px rgba(99, 102, 241, 0.05)',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: '0 12px 40px rgba(99, 102, 241, 0.15)',
            borderColor: '#6366f1',
          },
        }}
        onClick={() => handleItemClick(request)}
      >
        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header with Passenger & Budget */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  request.passenger.cancellationBadgeUntil && new Date(request.passenger.cancellationBadgeUntil) > new Date() ? (
                    <Typography
                      sx={{
                        width: 18,
                        height: 18,
                        bgcolor: '#ef4444',
                        color: 'white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: '900',
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      title={t('rideDetails.cancellationBadgeTooltip', 'Recent Cancellation Badge')}
                    >
                      C
                    </Typography>
                  ) : null
                }
              >
                <Avatar
                  src={request.passenger.profilePicture}
                  sx={{
                    width: 48,
                    height: 48,
                    cursor: 'pointer',
                    bgcolor: '#6366f1',
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                  }}
                  onClick={(e) => handleProfileClick(e, request.passenger.id)}
                >
                  {request.passenger.firstName[0]}
                </Avatar>
              </Badge>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1a1a1a' }}>
                  {request.passenger.preferredName || request.passenger.firstName}
                </Typography>
                {request.passenger.passengerRating && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Rating value={request.passenger.passengerRating} readOnly size="small" precision={0.1} sx={{ fontSize: '0.9rem' }} />
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              {request.maxBudget ? (
                <Typography variant="h4" sx={{
                  fontWeight: 900,
                  color: '#6366f1',
                  fontSize: '1.3rem',
                  letterSpacing: '-0.03em'
                }}>
                  ${request.minBudget ? `${request.minBudget}-` : ''}{request.maxBudget}
                </Typography>
              ) : (
                <Typography sx={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.9rem' }}>{t('searchResults.openBudget', 'Open budget')}</Typography>
              )}
            </Box>
          </Box>

          {/* Route Info */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mr: 2,
                height: '40px',
                justifyContent: 'space-between'
              }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', border: '2px solid #6366f1' }} />
                <Box sx={{ width: '2px', flex: 1, bgcolor: '#e5e7eb', my: 0.5 }} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6366f1' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a', lineHeight: 1.2, mb: 0.5 }}>
                  {request.originCity.name}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a', lineHeight: 1.2 }}>
                  {request.destinationCity.name}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

          {/* Date & Pass count */}
          <Box sx={{ mt: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '1.1rem' }}>
                <CalendarIcon sx={{ fontSize: 16 }} />
                {date}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '1.1rem' }}>
                <TimeIcon sx={{ fontSize: 16 }} />
                {time}
              </Typography>
            </Box>
            <Chip
              label={t('searchResults.passengers', { count: request.passengerCount })}
              size="small"
              sx={{
                width: '100%',
                bgcolor: 'rgba(99, 102, 241, 0.1)',
                color: '#6366f1',
                fontWeight: 800,
                fontSize: '0.9rem',
                height: '32px'
              }}
            />
          </Box>

          {/* Bid Interface */}
          {request.status === 'OPEN' && (
            <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <Divider sx={{ my: 3, borderStyle: 'dashed' }} />
              {isDriver ? (
                showBidForm ? (
                  <Box sx={{ bgcolor: '#f9fafb', p: 3, borderRadius: '20px' }}>
                    <Typography sx={{ fontWeight: 800, mb: 2, color: '#4b5563', fontSize: '0.9rem', textTransform: 'uppercase' }}>
                      {t('searchResults.sendOffer', 'Send an Offer')}
                    </Typography>
                    {requestBid.error && (
                      <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
                        {requestBid.error}
                      </Alert>
                    )}
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          label={t('searchResults.yourOfferLabel', 'Your Offer (CAD)')}
                          type="number"
                          fullWidth
                          value={requestBid.price}
                          onChange={(e) => {
                            setRequestBids(prev => ({
                              ...prev,
                              [request.id]: { ...requestBid, price: e.target.value, error: '' }
                            }));
                          }}
                          disabled={requestBid.submitting}
                          InputProps={{
                            startAdornment: <MoneyIcon sx={{ mr: 1, color: '#6366f1' }} />,
                            sx: { borderRadius: '14px', bgcolor: 'white' }
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 5 }}>
                        <TextField
                          label={t('searchResults.messageLabel', 'Message')}
                          fullWidth
                          value={requestBid.message}
                          onChange={(e) => {
                            setRequestBids(prev => ({
                              ...prev,
                              [request.id]: { ...requestBid, message: e.target.value, error: '' }
                            }));
                          }}
                          disabled={requestBid.submitting}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px', bgcolor: 'white' } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubmitBidInline(request);
                          }}
                          disabled={requestBid.submitting || !requestBid.price}
                          sx={{
                            py: 1.8,
                            borderRadius: '14px',
                            bgcolor: '#6366f1',
                            fontWeight: 800,
                            '&:hover': { bgcolor: '#4f46e5' }
                          }}
                        >
                          {requestBid.submitting ? t('searchResults.sending', 'Sending...') : t('searchResults.sendOffer', 'Send Offer')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                ) : null
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="text"
                    sx={{ color: '#6366f1', fontWeight: 700 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(request);
                    }}
                  >
                    {t('searchResults.viewDetailsSendOffer', 'View Details & Send Offer')}
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {isDriver && hasExistingBid && (() => {
            const existingBid = request.bids?.find(bid => bid.driver.id === user?.id);
            if (existingBid) {
              return (
                <Box sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: 'rgba(16, 185, 129, 0.05)',
                  borderRadius: '16px',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#10b981', textTransform: 'uppercase', mb: 0.5 }}>
                      {t('searchResults.yourOfferTitle', 'Your Offer')}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.25rem' }}>
                      ${existingBid.priceOffer}
                    </Typography>
                  </Box>
                  <Chip
                    label={
                      existingBid.status === 'ACCEPTED' ? t('searchResults.bidAccepted', 'Bid Accepted') :
                        existingBid.status === 'REJECTED' ? t('searchResults.bidRejected', 'Bid Rejected') :
                          t('searchResults.bidPending', 'Bid Pending')
                    }
                    color={existingBid.status === 'ACCEPTED' ? 'success' : 'warning'}
                    sx={{ fontWeight: 800, borderRadius: '8px' }}
                  />
                </Box>
              );
            }
            return null;
          })()}
        </CardContent>
      </Card>
    );
  };

  const renderResultCard = (item: SearchResult) => {
    if (isRide(item)) {
      return renderRideCard(item);
    } else {
      return renderRequestCard(item);
    }
  };

  const renderRideDetails = (ride: Ride) => {
    const { date, time } = formatDateTime(ride.departureDateTime);

    return (
      <Box>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar
                src={ride.driver.profilePicture}
                sx={{ width: 60, height: 60, cursor: 'pointer' }}
                onClick={(e) => handleProfileClick(e, ride.driver.id)}
              >
                {(ride.driver.preferredName || ride.driver.firstName || '?')[0]}{(ride.driver.lastName || '?')[0]}
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.875rem' }}>
                    {ride.driver.preferredName || ride.driver.firstName} {ride.driver.lastName ? ride.driver.lastName.charAt(0) + '.' : ''}
                  </Typography>
                  {ride.driver.isLicenseVerified && <VerifiedIcon sx={{ color: '#4caf50' }} />}
                </Box>
                <Rating value={ride.driver.driverRating || 0} readOnly precision={0.1} />
              </Box>
            </Box>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem' }}>
              {t('searchResults.rideDetails.origin', 'Origin')}
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>
              {ride.originCity.name}, {ride.originCity.province}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem' }}>
              {t('searchResults.rideDetails.destination', 'Destination')}
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem' }}>
              {ride.destinationCity.name}, {ride.destinationCity.province}
            </Typography>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TimeIcon sx={{ fontSize: '1.3125rem' }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.3125rem' }}>
                {t('searchResults.rideDetails.dateTime', 'Date & Time')}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontSize: '1.5rem' }}>
              {date} {t('searchResults.rideDetails.at', 'at')} {time}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem' }}>
              {t('searchResults.rideDetails.pricePerPerson', 'Price per person')}
            </Typography>
            <Typography variant="h5" color="primary.main" sx={{ fontSize: '2.25rem' }}>
              ${ride.pricePerPerson} CAD
            </Typography>
          </Grid>

          {ride.vehicle && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.3125rem' }}>
                    {t('searchResults.rideDetails.vehicle', 'Vehicle')}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontSize: '1.5rem' }}>
                  {ride.vehicle.color} {ride.vehicle.make.name} {ride.vehicle.model.name}
                </Typography>
              </Grid>
            </>
          )}

          {/* Additional Details */}
          {ride.originDetails && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem' }}>
                  {t('searchResults.rideDetails.pickupDetails', 'Pickup Details')}
                </Typography>
                <TranslatingText
                  text={ride.originDetails}
                  variant="body1"
                  sx={{ fontSize: '1.5rem' }}
                />
              </Grid>
            </>
          )}

          {ride.destinationDetails && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem' }}>
                  {t('searchResults.rideDetails.dropoffDetails', 'Drop-off Details')}
                </Typography>
                <TranslatingText
                  text={ride.destinationDetails}
                  variant="body1"
                  sx={{ fontSize: '1.5rem' }}
                />
              </Grid>
            </>
          )}

          {(ride.allowsLargeLuggage || ride.allowsPets || ride.allowsSmoking) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem' }}>
                  {t('searchResults.rideDetails.tripFeatures', 'Trip Features')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {ride.allowsLargeLuggage && (
                    <Chip
                      icon={<LuggageIcon sx={{ fontSize: '1.3125rem' }} />}
                      label={t('searchResults.rideDetails.largeLuggage', 'Large Luggage')}
                      size="small"
                      color="primary"
                      sx={{ fontSize: '1.3125rem' }}
                    />
                  )}
                  {ride.allowsPets && (
                    <Chip
                      icon={<PetsIcon sx={{ fontSize: '1.3125rem' }} />}
                      label={t('searchResults.rideDetails.petsAllowed', 'Pets Allowed')}
                      size="small"
                      color="primary"
                      sx={{ fontSize: '1.3125rem' }}
                    />
                  )}
                  {ride.allowsSmoking && (
                    <Chip
                      icon={<SmokingIcon sx={{ fontSize: '1.3125rem' }} />}
                      label={t('searchResults.rideDetails.smokingAllowed', 'Smoking Allowed')}
                      size="small"
                      color="primary"
                      sx={{ fontSize: '1.3125rem' }}
                    />
                  )}
                </Box>
              </Grid>
            </>
          )}

          {ride.estimatedDurationMinutes && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem' }}>
                  {t('searchResults.rideDetails.estimatedDuration', 'Estimated Duration')}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.5rem' }}>
                  {Math.floor(ride.estimatedDurationMinutes / 60)}h {ride.estimatedDurationMinutes % 60}m
                </Typography>
              </Grid>
            </>
          )}

          {ride.additionalNotes && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem' }}>
                  {t('searchResults.rideDetails.additionalNotes', 'Additional Notes')}
                </Typography>
                <TranslatingText
                  text={ride.additionalNotes}
                  variant="body1"
                  sx={{ fontSize: '1.5rem' }}
                />
              </Grid>
            </>
          )}

          {/* Ask Question Button - Show if allowChat is true and user is not the driver */}
          {ride.allowChat !== false && user && user.id !== ride.driver.id && (
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <MessageButton
                  userId={ride.driver.id}
                  userName={`${ride.driver.preferredName || ride.driver.firstName || ''} ${ride.driver.lastName ? ride.driver.lastName.charAt(0) + '.' : ''}`}
                  contextType="ride"
                  contextId={ride.id}
                  label={t('searchResults.rideDetails.askQuestion', 'Ask Question About This Ride')}
                  variant="outlined"
                  color="primary"
                  size="large"
                  sx={{
                    py: 1.5,
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
            </Grid>
          )}

          {/* Join Ride Button */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 2 }} />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={joiningRide || !user || ride.availableSeats <= 0}
              onClick={async () => {
                if (!user) {
                  navigate('/login', { state: { returnUrl: location.pathname } });
                  return;
                }

                setJoiningRide(true);
                try {
                  // Send join request (booking with status based on manual approval)
                  const response = await apiService.post('/bookings', {
                    rideId: ride.id,
                    seatsBooked: 1,
                  });

                  if (response.success) {
                    setDetailDialogOpen(false);
                    navigate('/my-rides', { state: { tab: 0 } }); // Navigate to My Rides with Active tab
                  } else {
                    alert(response.message || t('searchResults.rideDetails.joinError', 'Failed to join ride'));
                  }
                } catch (error: any) {
                  console.error('Join ride error:', error);
                  alert(error.response?.data?.message || t('searchResults.rideDetails.joinError', 'Failed to join ride'));
                } finally {
                  setJoiningRide(false);
                }
              }}
              sx={{
                py: 1.5,
                fontSize: '1.875rem',
                fontWeight: 'bold'
              }}
            >
              {joiningRide ? t('searchResults.rideDetails.joining', 'Joining...') : t('searchResults.rideDetails.joinRide', 'Join the Ride')}
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const handleItemClick = (item: SearchResult) => {
    setSelectedItem(item);
    // Reset bid form when opening modal
    if (isRideRequest(item)) {
      setDetailBidPrice('');
      setDetailBidMessage('');
      setDetailBidError('');
    }
    setDetailDialogOpen(true);
  };

  const handleSubmitDetailBid = async (request: RideRequest) => {
    const price = parseFloat(detailBidPrice);

    if (isNaN(price) || price <= 0) {
      setDetailBidError(t('searchResults.requestDetails.priceError', 'Please enter a valid price'));
      return;
    }

    if (request.minBudget && price < request.minBudget) {
      setDetailBidError(t('searchResults.requestDetails.minPriceError', { price: request.minBudget }));
      return;
    }

    if (request.maxBudget && price > request.maxBudget) {
      setDetailBidError(t('searchResults.requestDetails.maxPriceError', { price: request.maxBudget }));
      return;
    }

    setSubmittingDetailBid(true);
    setDetailBidError('');

    try {
      const bidData = {
        requestId: request.id,
        priceOffer: price,
        message: detailBidMessage.trim() || undefined
      };

      const response = await apiService.post('/bids', bidData);

      if (response.success) {
        // Refresh the request to show the new bid
        try {
          const updatedResponse = await apiService.get(`/requests/${request.id}`);
          if (updatedResponse.success && updatedResponse.data?.request) {
            // Update selectedItem to show the new bid in the modal
            setSelectedItem(updatedResponse.data.request);
            // Also update the results list if needed
            // Note: We can't directly update results here as it's a prop
            // The parent component should handle refreshing results
          }
        } catch (refreshError) {
          console.error('Error refreshing request:', refreshError);
        }

        setDetailBidPrice('');
        setDetailBidMessage('');
        setDetailBidError('');
        // Don't close the modal automatically - let user see the success
        // setDetailDialogOpen(false);
        // setSelectedItem(null);
      }
    } catch (error: any) {
      console.error('Submit bid error:', error);
      setDetailBidError(error.response?.data?.message || t('searchResults.requestDetails.bidError', 'Failed to submit bid'));
    } finally {
      setSubmittingDetailBid(false);
    }
  };

  const renderRequestDetails = (request: RideRequest) => {
    const { date, time } = formatDateTime(request.preferredDateTime);
    const hasExistingBid = isDriver && request.bids?.some(bid => bid.driver.id === user?.id);
    const showBidForm = isDriver && !hasExistingBid && request.status === 'OPEN';

    return (
      <Box>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Avatar
                src={request.passenger.profilePicture}
                sx={{ width: 60, height: 60, cursor: 'pointer' }}
                onClick={(e) => handleProfileClick(e, request.passenger.id)}
              >
                {(request.passenger.preferredName || request.passenger.firstName || '?')[0]}{(request.passenger.lastName || '?')[0]}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontSize: '2.25rem' }}>
                  {request.passenger.preferredName || request.passenger.firstName} {request.passenger.lastName ? request.passenger.lastName.charAt(0) + '.' : ''}
                </Typography>
                {request.passenger.passengerRating && (
                  <Rating value={request.passenger.passengerRating} readOnly precision={0.1} sx={{ '& .MuiRating-icon': { fontSize: '1.5rem' } }} />
                )}
              </Box>
            </Box>
          </Grid>

          <Divider sx={{ my: 1 }} />

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem', mb: 0.5 }}>
              {t('searchResults.rideDetails.origin', 'Origin')}
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem', mb: 0.5 }}>
              {request.originCity.name}, {request.originCity.province}
            </Typography>
            {request.originDetails && (
              <TranslatingText
                text={request.originDetails}
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, fontSize: '1.3125rem' }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem', mb: 0.5 }}>
              {t('searchResults.rideDetails.destination', 'Destination')}
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem', mb: 0.5 }}>
              {request.destinationCity.name}, {request.destinationCity.province}
            </Typography>
            {request.destinationDetails && (
              <TranslatingText
                text={request.destinationDetails}
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, fontSize: '1.3125rem' }}
              />
            )}
          </Grid>

          <Divider sx={{ my: 1 }} />

          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <TimeIcon sx={{ fontSize: '1.3125rem' }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.3125rem' }}>
                {t('searchResults.requestDetails.preferredDateTime', 'Preferred Date & Time')}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontSize: '1.5rem' }}>
              {date} {t('searchResults.rideDetails.at', 'at')} {time}
            </Typography>
            {request.timeFlexibility > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '1.3125rem' }}>
                {t('searchResults.requestDetails.flexibility', { hours: request.timeFlexibility })}
              </Typography>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem', mb: 0.5 }}>
              {t('searchResults.requestDetails.numberOfPassengers', 'Number of Passengers')}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.5rem' }}>
              {request.passengerCount} {t(request.passengerCount === 1 ? 'searchResults.requestDetails.passenger_one' : 'searchResults.requestDetails.passenger_other', { count: request.passengerCount })}
            </Typography>
          </Grid>

          <Divider sx={{ my: 1 }} />

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem', mb: 0.5 }}>
              {t('searchResults.requestDetails.budget', 'Budget')}
            </Typography>
            {request.minBudget && request.maxBudget ? (
              <Typography variant="h6" color="secondary.main" sx={{ fontSize: '1.875rem' }}>
                {t('searchResults.requestDetails.range', { min: request.minBudget, max: request.maxBudget })}
              </Typography>
            ) : request.maxBudget ? (
              <Typography variant="h6" color="secondary.main" sx={{ fontSize: '1.875rem' }}>
                {t('searchResults.requestDetails.upTo', { price: request.maxBudget })}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.3125rem' }}>
                {t('searchResults.requestDetails.noBudget', 'No budget specified')}
              </Typography>
            )}
          </Grid>

          {/* Special Requirements */}
          {(request.needsLargeLuggage || request.needsChildSeat || request.needsWheelchairAccess) && (
            <>
              <Divider sx={{ my: 1 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem', mb: 0.5 }}>
                  {t('searchResults.requestDetails.specialRequirements', 'Special Requirements')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {request.needsLargeLuggage && (
                    <Chip
                      icon={<LuggageIcon sx={{ fontSize: '1.3125rem' }} />}
                      label={t('searchResults.requestDetails.largeLuggage', 'Large Luggage')}
                      size="small"
                      color="primary"
                      sx={{ fontSize: '1.3125rem' }}
                    />
                  )}
                  {request.needsChildSeat && (
                    <Chip
                      icon={<ChildCareIcon sx={{ fontSize: '1.3125rem' }} />}
                      label={t('searchResults.requestDetails.childSeat', 'Child Seat')}
                      size="small"
                      color="primary"
                      sx={{ fontSize: '1.3125rem' }}
                    />
                  )}
                  {request.needsWheelchairAccess && (
                    <Chip
                      icon={<AccessibleIcon sx={{ fontSize: '1.3125rem' }} />}
                      label={t('searchResults.requestDetails.wheelchairAccess', 'Wheelchair Access')}
                      size="small"
                      color="primary"
                      sx={{ fontSize: '1.3125rem' }}
                    />
                  )}
                </Box>
              </Grid>
            </>
          )}

          {/* Special Requirements Text */}
          {request.specialRequirements && (
            <>
              <Divider sx={{ my: 1 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem', mb: 0.5 }}>
                  {t('searchResults.requestDetails.specialRequirementsDetails', 'Special Requirements Details')}
                </Typography>
                <TranslatingText
                  text={request.specialRequirements}
                  variant="body2"
                  sx={{ fontSize: '1.3125rem' }}
                />
              </Grid>
            </>
          )}

          {/* Additional Information */}
          {request.description && (
            <>
              <Divider sx={{ my: 1 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem', mb: 0.5 }}>
                  {t('searchResults.requestDetails.additionalInfo', 'Additional Information')}
                </Typography>
                <TranslatingText
                  text={request.description}
                  variant="body2"
                  sx={{ fontSize: '1.3125rem' }}
                />
              </Grid>
            </>
          )}

          {/* Ask Question Button - Show if allowChat is true and user is not the passenger */}
          {request.allowChat !== false && user && user.id !== request.passenger.id && (
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <MessageButton
                  userId={request.passenger.id}
                  userName={`${request.passenger.preferredName || request.passenger.firstName || ''} ${request.passenger.lastName ? request.passenger.lastName.charAt(0) + '.' : ''}`}
                  contextType="request"
                  contextId={request.id}
                  label={t('searchResults.requestDetails.askQuestion', 'Ask Question About This Ride Request')}
                  variant="outlined"
                  color="secondary"
                  size="large"
                  sx={{
                    py: 1.5,
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
            </Grid>
          )}

          {/* Bid Form for Drivers or Become Driver Button */}
          {request.status === 'OPEN' && (
            <>
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1.5 }} />
                {isDriver ? (
                  showBidForm ? (
                    <>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: '1.875rem', mb: 0.5 }}>
                        {t('searchResults.requestDetails.placeBid', 'Place Your Bid')}
                      </Typography>
                      {detailBidError && (
                        <Alert severity="error" sx={{ mb: 1, fontSize: '1.3125rem', '& .MuiAlert-message': { fontSize: '1.3125rem' } }}>
                          {detailBidError}
                        </Alert>
                      )}
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          label={t('searchResults.requestDetails.yourPriceOffer', 'Your Price Offer (CAD)')}
                          type="number"
                          fullWidth
                          value={detailBidPrice}
                          onChange={(e) => {
                            setDetailBidPrice(e.target.value);
                            setDetailBidError('');
                          }}
                          disabled={submittingDetailBid}
                          sx={{ mb: 1, '& .MuiInputLabel-root': { fontSize: '1.3125rem' }, '& .MuiInputBase-input': { fontSize: '1.3125rem' }, '& .MuiFormHelperText-root': { fontSize: '1rem' } }}
                          InputProps={{
                            startAdornment: <MoneyIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.3125rem' }} />,
                          }}
                          helperText={
                            request.minBudget && request.maxBudget
                              ? t('searchResults.requestDetails.rangeError', { min: request.minBudget, max: request.maxBudget })
                              : request.maxBudget
                                ? t('searchResults.requestDetails.maxPriceError', { price: request.maxBudget })
                                : ''
                          }
                        />
                        <TextField
                          label={t('searchResults.requestDetails.messageOptional', 'Message (Optional)')}
                          multiline
                          rows={3}
                          fullWidth
                          value={detailBidMessage}
                          onChange={(e) => setDetailBidMessage(e.target.value)}
                          disabled={submittingDetailBid}
                          placeholder={t('searchResults.requestDetails.messagePlaceholder', 'Add a message to the passenger...')}
                          sx={{ mb: 1, '& .MuiInputLabel-root': { fontSize: '1.3125rem' }, '& .MuiInputBase-input': { fontSize: '1.3125rem' } }}
                        />
                        <Button
                          variant="contained"
                          color="secondary"
                          fullWidth
                          onClick={() => handleSubmitDetailBid(request)}
                          disabled={submittingDetailBid || !detailBidPrice}
                          sx={{ py: 1, fontSize: '1.5rem' }}
                        >
                          {submittingDetailBid ? t('searchResults.requestDetails.submitting', 'Submitting...') : t('searchResults.requestDetails.submitBid', 'Submit Bid')}
                        </Button>
                      </Box>
                    </>
                  ) : hasExistingBid ? (
                    <Box>
                      <Alert severity="success" sx={{ fontSize: '1.3125rem', '& .MuiAlert-message': { fontSize: '1.3125rem' }, mb: 2 }}>
                        {t('searchResults.requestDetails.alreadyBid', 'You have already submitted a bid for this request.')}
                      </Alert>
                      {(() => {
                        const existingBid = request.bids?.find(bid => bid.driver.id === user?.id);
                        if (existingBid) {
                          return (
                            <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.3125rem', mb: 1 }}>
                                {t('searchResults.requestDetails.yourBidDetails', 'Your Bid Details')}
                              </Typography>
                              <Typography variant="h6" color="success.dark" sx={{ fontSize: '1.875rem', mb: 1 }}>
                                {t('searchResults.requestDetails.offeredPrice', { price: existingBid.priceOffer })}
                              </Typography>
                              {existingBid.message && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.3125rem', mb: 0.5 }}>
                                    {t('searchResults.requestDetails.yourMessage', 'Your Message:')}
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontSize: '1.5rem' }}>
                                    {existingBid.message}
                                  </Typography>
                                </Box>
                              )}
                              <Chip
                                label={
                                  existingBid.status === 'ACCEPTED' ? t('searchResults.bidAccepted', 'Bid Accepted') :
                                    existingBid.status === 'REJECTED' ? t('searchResults.bidRejected', 'Bid Rejected') :
                                      t('searchResults.bidPending', 'Bid Pending')
                                }
                                color={existingBid.status === 'ACCEPTED' ? 'success' : existingBid.status === 'REJECTED' ? 'error' : 'warning'}
                                size="small"
                                sx={{ mt: 1, fontSize: '1.125rem' }}
                              />
                            </Box>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                  ) : null
                ) : user ? (
                  <Alert severity="info" sx={{ fontSize: '1.3125rem', '& .MuiAlert-message': { fontSize: '1.3125rem' } }}>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '1.3125rem' }}>
                      {t('searchResults.requestDetails.becomeDriverInfo', 'You need to become a driver to place bids on ride requests.')}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleBecomeDriverRedirect}
                      sx={{ fontSize: '1.3125rem' }}
                    >
                      {t('searchResults.requestDetails.becomeDriver', 'Become a Driver')}
                    </Button>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ fontSize: '1.3125rem', '& .MuiAlert-message': { fontSize: '1.3125rem' } }}>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '1.3125rem' }}>
                      {t('searchResults.requestDetails.loginInfo', 'Please log in and become a driver to place bids on ride requests.')}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        const returnUrl = location.pathname + location.search;
                        navigate('/login', { state: { returnUrl } });
                      }}
                      sx={{ fontSize: '1.3125rem' }}
                    >
                      {t('searchResults.requestDetails.login', 'Log In')}
                    </Button>
                  </Alert>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: '32px',
            bgcolor: '#f8fafc',
            backgroundImage: 'none',
            overflow: 'hidden',
          },
        }}
        BackdropProps={{
          sx: {
            bgcolor: 'rgba(15, 23, 42, 0.4)',
            backdropFilter: 'blur(8px)',
          }
        }}
      >
        <DialogTitle sx={{
          p: 4,
          pb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'white',
          borderBottom: '1px solid rgba(0,0,0,0.05)'
        }}>
          <Box>
            <Typography variant="h4" sx={{
              fontWeight: 900,
              letterSpacing: '-0.03em',
              color: '#1e293b'
            }}>
              {t('searchResults.title', 'Search Results')}
            </Typography>
            {originCity && destinationCity && (
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600, fontSize: '1.1rem', mt: 0.5 }}>
                {originCity} → {destinationCity}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: '#f1f5f9',
              '&:hover': { bgcolor: '#e2e8f0' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 4, borderRadius: '16px' }}>
              {error}
            </Alert>
          )}

          {/* Filters and View Toggle */}
          <Box sx={{
            mb: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            bgcolor: 'rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(10px)',
            p: 3,
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.05)'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
              {/* Left Side: Results Count */}
              <Typography variant="h5" sx={{
                fontSize: '1.75rem',
                fontWeight: 800,
                color: '#1a1a1a',
                letterSpacing: '-0.02em',
                mr: 2
              }}>
                {searchType === 'requested'
                  ? t(displayResults.length !== 1 ? 'searchResults.found_request_other' : 'searchResults.found_request_one', { count: displayResults.length })
                  : t(displayResults.length !== 1 ? 'searchResults.found_ride_other' : 'searchResults.found_ride_one', { count: displayResults.length })
                }
              </Typography>

              {/* Right Side: Filters & Toggles Consolidated */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>

                {/* View Mode Toggle */}
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => {
                    if (newMode !== null) {
                      setViewMode(newMode);
                      if (newMode === 'calendar') {
                        // Find the earliest date (first month with a ride)
                        let minDate: Date | null = null;

                        filteredResults.forEach(item => {
                          const dateTime = isRide(item) ? item.departureDateTime : item.preferredDateTime;
                          const date = new Date(dateTime);

                          if (!minDate || date < minDate) {
                            minDate = date;
                          }
                        });

                        setSelectedDate(minDate);
                      } else {
                        setSelectedDate(null);
                      }
                    }
                  }}
                  sx={{
                    bgcolor: 'rgba(0,0,0,0.05)',
                    borderRadius: '16px',
                    p: 0.5,
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      borderRadius: '12px !important',
                      px: 3,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '1.4rem', // Increased size
                      color: '#6b7280',
                      transition: 'all 0.2s',
                      '&.Mui-selected': {
                        bgcolor: '#ffffff',
                        color: '#10b981',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        '&:hover': {
                          bgcolor: '#ffffff',
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="list">
                    <ListIcon sx={{ mr: 1, fontSize: 24 }} />
                    {t('searchResults.list', 'List')}
                  </ToggleButton>
                  <ToggleButton value="calendar">
                    <CalendarIcon sx={{ mr: 1, fontSize: 24 }} />
                    {t('searchResults.calendar', 'Calendar')}
                  </ToggleButton>
                </ToggleButtonGroup>

                <Divider orientation="vertical" flexItem sx={{ height: 40, alignSelf: 'center', opacity: 0.3 }} />

                {/* Sort By */}
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel sx={{ fontWeight: 600, fontSize: '1.3rem', top: '-6px' }}>{t('searchResults.sortBy', 'Sort by')}</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'price' | 'date')}
                    label={t('searchResults.sortBy', 'Sort by')}
                    sx={{
                      borderRadius: '14px',
                      bgcolor: 'white',
                      fontSize: '1.4rem',
                      height: '56px',
                      '& .MuiOutlinedInput-notchedOutline': { border: '1px solid rgba(0,0,0,0.1)' }
                    }}
                  >
                    <MenuItem value="date" sx={{ fontSize: '1.3rem' }}>{t('searchResults.latestDate', 'Latest Date')}</MenuItem>
                    <MenuItem value="price" sx={{ fontSize: '1.3rem' }}>{t('searchResults.lowestPrice', 'Lowest Price')}</MenuItem>
                  </Select>
                </FormControl>

                {/* Specific Date Filter */}
                {viewMode === 'list' && (
                  <TextField
                    type="date"
                    label={t('searchResults.specificDate', 'Specific Date')}
                    value={dateFilter ? dateFilter.toISOString().split('T')[0] : ''}
                    onChange={(e) => setDateFilter(e.target.value ? new Date(e.target.value) : null)}
                    InputLabelProps={{ shrink: true, sx: { fontSize: '1.3rem', top: '-6px' } }}
                    InputProps={{ sx: { fontSize: '1.4rem', height: '56px' } }}
                    sx={{
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '14px',
                        bgcolor: 'white',
                        '& fieldset': { border: '1px solid rgba(0,0,0,0.1)' }
                      }
                    }}
                  />
                )}

                {/* Price Filter (Keeping compact to fit line) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'white', p: 0.5, borderRadius: '14px', border: '1px solid rgba(0,0,0,0.1)', height: '56px' }}>
                  <TextField
                    variant="standard"
                    type="number"
                    placeholder={t('searchResults.minPrice', 'Min $')}
                    value={priceFilter.min || ''}
                    onChange={(e) => setPriceFilter({ ...priceFilter, min: e.target.value ? Number(e.target.value) : undefined })}
                    InputProps={{
                      disableUnderline: true,
                      startAdornment: <MoneyIcon sx={{ mr: 1, fontSize: 20, color: '#9ca3af' }} />,
                      sx: { fontSize: '1.3rem' }
                    }}
                    sx={{ width: 100, px: 2 }}
                  />
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <TextField
                    variant="standard"
                    type="number"
                    placeholder={t('searchResults.maxPrice', 'Max $')}
                    value={priceFilter.max || ''}
                    onChange={(e) => setPriceFilter({ ...priceFilter, max: e.target.value ? Number(e.target.value) : undefined })}
                    InputProps={{
                      disableUnderline: true,
                      startAdornment: <MoneyIcon sx={{ mr: 1, fontSize: 20, color: '#9ca3af' }} />,
                      sx: { fontSize: '1.3rem' }
                    }}
                    sx={{ width: 100, px: 2 }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* List View */}
          {viewMode === 'list' && (
            <Box sx={{ pb: 2 }}>
              {displayResults.length > 0 ? (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(280px, 1fr))' },
                  gap: 3
                }}>
                  {displayResults.map(renderResultCard)}
                </Box>
              ) : (
                <Box sx={{
                  py: 12,
                  textAlign: 'center',
                  bgcolor: 'white',
                  borderRadius: '32px',
                  border: '2px dashed rgba(0,0,0,0.05)'
                }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: { xs: 4, sm: 8 } }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#94a3b8', mb: 2 }}>
                        {t('searchResults.emptyState.beFirstDriver')}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/create-ride')}
                        sx={{ fontSize: '1.5rem', py: 1.5, minWidth: '250px', borderRadius: '12px' }}
                      >
                        {t('searchResults.emptyState.offerRide')}
                      </Button>
                    </Box>

                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#94a3b8', mb: 2 }}>
                        {t('searchResults.emptyState.postRequest')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => navigate('/request-ride')}
                          sx={{ fontSize: '1.5rem', py: 1.5, minWidth: '250px', borderRadius: '12px', borderColor: '#94a3b8', color: '#94a3b8' }}
                        >
                          {t('searchResults.emptyState.requestRide')}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Calendar View */}
          {viewMode === 'calendar' && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: '500px' } }}>
                  <Paper sx={{
                    p: 2,
                    borderRadius: '24px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
                    overflow: 'hidden',
                    // Compensate for the scale
                    minHeight: '450px'
                  }}>
                    <Box sx={{
                      transform: 'scale(1.5)',
                      transformOrigin: 'top left',
                      width: '66.66%', // 100/1.5
                      '& *': { fontWeight: 'bold !important' },
                      '& .MuiPickersDay-root': { fontWeight: 'bold !important' },
                      '& .MuiDayCalendar-weekDayLabel': { fontWeight: '900', color: '#10b981' }
                    }}>
                      <DateCalendar
                        value={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        slots={{
                          day: CustomDay,
                        }}
                        disablePast
                      />
                    </Box>
                  </Paper>
                </Box>
                <Box sx={{ flex: 1 }}>
                  {selectedDate ? (
                    displayResults.length > 0 ? (
                      <Box>
                        {displayResults.map(renderResultCard)}
                      </Box>
                    ) : (
                      <Paper sx={{ p: 6, textAlign: 'center', borderRadius: '24px' }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: { xs: 4, sm: 8 } }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#94a3b8', mb: 2 }}>
                              {t('searchResults.emptyState.beFirstDriver')}
                            </Typography>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => navigate('/create-ride')}
                              sx={{ fontSize: '1.2rem', py: 1.5, minWidth: '200px', borderRadius: '12px' }}
                            >
                              {t('searchResults.emptyState.offerRide')}
                            </Button>
                          </Box>

                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#94a3b8', mb: 2 }}>
                              {t('searchResults.emptyState.postRequest')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => navigate('/request-ride')}
                                sx={{ fontSize: '1.2rem', py: 1.5, minWidth: '200px', borderRadius: '12px', borderColor: '#94a3b8', color: '#94a3b8' }}
                              >
                                {t('searchResults.emptyState.requestRide')}
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    )
                  ) : (
                    <Paper sx={{ p: 8, textAlign: 'center', borderRadius: '24px' }}>
                      <CalendarIcon sx={{ fontSize: 80, color: '#e2e8f0', mb: 2 }} />
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#94a3b8' }}>
                        {t('searchResults.selectDate', 'Select a date')}
                      </Typography>
                    </Paper>
                  )}
                </Box>
              </Box>
            </LocalizationProvider>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
          <Button
            onClick={onClose}
            sx={{
              color: '#64748b',
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '1rem',
              px: 4
            }}
          >
            {t('searchResults.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Profile Modal */}


      {/* Ride Details Modal (Modern) */}
      {selectedItem && isRide(selectedItem) && (
        <RideDetailsModal
          isOpen={detailDialogOpen}
          onClose={() => {
            setDetailDialogOpen(false);
            setSelectedItem(null);
          }}
          ride={selectedItem}
          onJoin={(rideId: string, seats: number) => {
            setDetailDialogOpen(false);
            navigate(`/ride/${rideId}`, {
              state: {
                ride: selectedItem,
                showBooking: true,
                initialSeats: seats
              }
            });
          }}
        />
      )}

      {/* Detail Dialog (For Requests Only) */}
      <Dialog
        open={detailDialogOpen && !!selectedItem && !isRide(selectedItem)}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedItem(null);
          setDetailBidPrice('');
          setDetailBidMessage('');
          setDetailBidError('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedItem && isRideRequest(selectedItem) ? t('searchResults.requestDetails.title', 'Ride Request Details') : t('searchResults.rideDetails.details', 'Details')}
            </Typography>
            <IconButton onClick={() => {
              setDetailDialogOpen(false);
              setSelectedItem(null);
              setDetailBidPrice('');
              setDetailBidMessage('');
              setDetailBidError('');
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedItem && isRideRequest(selectedItem) && renderRequestDetails(selectedItem)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDetailDialogOpen(false);
            setSelectedItem(null);
            setDetailBidPrice('');
            setDetailBidMessage('');
            setDetailBidError('');
          }}>
            {t('searchResults.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ResultsModal;

